// src/hooks/queries/useKanban.ts
/**
 * Hooks de TanStack Query para Kanban.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kanbanService } from '@/services/kanban';
import type {
  TipoTableroKanban,
  AgrupacionKanban,
  TableroCreate,
  TableroUpdate,
  MoverTarjetaRequest,
  MoverMultiplesRequest,
  ReordenarColumnaRequest,
  ColumnaConfig,
} from '@/types/kanban';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const kanbanKeys = {
  all: ['kanban'] as const,
  tableros: () => [...kanbanKeys.all, 'tableros'] as const,
  tablerosList: (tipo?: TipoTableroKanban) => [...kanbanKeys.tableros(), { tipo }] as const,
  tablero: (id: string) => [...kanbanKeys.tableros(), id] as const,
  tableroDefault: (tipo: TipoTableroKanban) => [...kanbanKeys.tableros(), 'default', tipo] as const,
  estadisticas: (tableroId: string) => [...kanbanKeys.tablero(tableroId), 'estadisticas'] as const,
  agrupaciones: (tipo: TipoTableroKanban) => [...kanbanKeys.all, 'agrupaciones', tipo] as const,
  valoresAgrupacion: (agrupacion: AgrupacionKanban, filtros?: Record<string, string>) =>
    [...kanbanKeys.all, 'agrupaciones', 'valores', agrupacion, filtros] as const,
};

// =============================================================================
// TABLEROS - QUERIES
// =============================================================================

/**
 * Lista los tableros del usuario.
 * @param tipo - Opcional: filtrar por tipo de tablero
 */
export function useTableros(tipo?: TipoTableroKanban) {
  return useQuery({
    queryKey: kanbanKeys.tablerosList(tipo),
    queryFn: () => kanbanService.listTableros(tipo),
  });
}

/**
 * Obtiene un tablero especifico con sus columnas y tarjetas.
 * @param id - ID del tablero
 */
export function useTablero(id: string) {
  return useQuery({
    queryKey: kanbanKeys.tablero(id),
    queryFn: () => kanbanService.getTablero(id),
    enabled: !!id,
  });
}

/**
 * Obtiene el tablero por defecto para un tipo.
 * @param tipo - Tipo de tablero
 */
export function useTableroDefault(tipo: TipoTableroKanban) {
  return useQuery({
    queryKey: kanbanKeys.tableroDefault(tipo),
    queryFn: () => kanbanService.getTableroDefault(tipo),
    enabled: !!tipo,
  });
}

// =============================================================================
// TABLEROS - MUTATIONS
// =============================================================================

/**
 * Mutation para crear un nuevo tablero.
 */
export function useCreateTablero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TableroCreate) => kanbanService.createTablero(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tableros() });
      // Invalidar default si es el primer tablero del tipo
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tableroDefault(variables.tipo) });
    },
  });
}

/**
 * Mutation para actualizar un tablero.
 */
export function useUpdateTablero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TableroUpdate }) =>
      kanbanService.updateTablero(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tableros() });
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(id) });
    },
  });
}

/**
 * Mutation para eliminar un tablero.
 */
export function useDeleteTablero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => kanbanService.deleteTablero(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tableros() });
    },
  });
}

/**
 * Mutation para duplicar un tablero.
 */
export function useDuplicarTablero() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nombre }: { id: string; nombre: string }) =>
      kanbanService.duplicarTablero(id, nombre),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tableros() });
    },
  });
}

/**
 * Mutation para establecer un tablero como default.
 */
export function useSetTableroDefault() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => kanbanService.setTableroDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tableros() });
    },
  });
}

// =============================================================================
// MOVIMIENTO DE TARJETAS
// =============================================================================

/**
 * Mutation para mover una tarjeta a otra columna/posicion.
 */
export function useMoverTarjeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tableroId, data }: { tableroId: string; data: MoverTarjetaRequest }) =>
      kanbanService.moverTarjeta(tableroId, data),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
      queryClient.invalidateQueries({ queryKey: kanbanKeys.estadisticas(tableroId) });
    },
  });
}

/**
 * Mutation para mover multiples tarjetas en una operacion.
 */
export function useMoverMultiples() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tableroId, data }: { tableroId: string; data: MoverMultiplesRequest }) =>
      kanbanService.moverMultiples(tableroId, data),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
      queryClient.invalidateQueries({ queryKey: kanbanKeys.estadisticas(tableroId) });
    },
  });
}

/**
 * Mutation para reordenar tarjetas dentro de una columna.
 */
export function useReordenarColumna() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tableroId, data }: { tableroId: string; data: ReordenarColumnaRequest }) =>
      kanbanService.reordenarColumna(tableroId, data),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
    },
  });
}

// =============================================================================
// COLUMNAS
// =============================================================================

/**
 * Mutation para actualizar la configuracion de una columna.
 */
export function useActualizarColumna() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tableroId,
      columna,
      config,
    }: {
      tableroId: string;
      columna: string;
      config: Partial<ColumnaConfig>;
    }) => kanbanService.actualizarColumna(tableroId, columna, config),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
    },
  });
}

/**
 * Mutation para colapsar o expandir una columna.
 */
export function useColapsarColumna() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tableroId,
      columna,
      colapsada,
    }: {
      tableroId: string;
      columna: string;
      colapsada: boolean;
    }) => kanbanService.colapsarColumna(tableroId, columna, colapsada),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
    },
  });
}

/**
 * Mutation para ocultar una columna.
 */
export function useOcultarColumna() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tableroId, columna }: { tableroId: string; columna: string }) =>
      kanbanService.ocultarColumna(tableroId, columna),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
    },
  });
}

/**
 * Mutation para mostrar una columna oculta.
 */
export function useMostrarColumna() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tableroId, columna }: { tableroId: string; columna: string }) =>
      kanbanService.mostrarColumna(tableroId, columna),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
    },
  });
}

/**
 * Mutation para reordenar las columnas del tablero.
 */
export function useReordenarColumnas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tableroId, orden }: { tableroId: string; orden: string[] }) =>
      kanbanService.reordenarColumnas(tableroId, orden),
    onSuccess: (_, { tableroId }) => {
      queryClient.invalidateQueries({ queryKey: kanbanKeys.tablero(tableroId) });
    },
  });
}

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Obtiene las agrupaciones disponibles para un tipo de tablero.
 * @param tipo - Tipo de tablero (undefined skipea la query via enabled)
 */
export function useAgrupaciones(tipo: TipoTableroKanban | undefined) {
  return useQuery({
    queryKey: kanbanKeys.agrupaciones(tipo as TipoTableroKanban),
    queryFn: () => {
      // Defense-in-depth: enabled:!!tipo debería prevenir esta ruta, pero si
      // alguien invoca refetch()/prefetchQuery() bypassing enabled, fallamos
      // ruidoso en vez de mandar undefined al backend (silent failure).
      if (!tipo) throw new Error('useAgrupaciones invoked without tipo (enabled guard bypassed)');
      return kanbanService.getAgrupaciones(tipo);
    },
    enabled: !!tipo,
  });
}

/**
 * Obtiene los valores disponibles para una agrupacion.
 * @param agrupacion - Tipo de agrupacion
 * @param filtros - Filtros adicionales
 */
export function useValoresAgrupacion(
  agrupacion: AgrupacionKanban,
  filtros?: Record<string, string>
) {
  return useQuery({
    queryKey: kanbanKeys.valoresAgrupacion(agrupacion, filtros),
    queryFn: () => kanbanService.getValoresAgrupacion(agrupacion, filtros),
    enabled: !!agrupacion,
  });
}

/**
 * Obtiene estadisticas de un tablero.
 * @param tableroId - ID del tablero
 */
export function useEstadisticasTablero(tableroId: string) {
  return useQuery({
    queryKey: kanbanKeys.estadisticas(tableroId),
    queryFn: () => kanbanService.getEstadisticas(tableroId),
    enabled: !!tableroId,
  });
}
